/**
 * Validiert ein Eingabeobjekt gegen die QualifiedApplicationRequest-Regeln
 * @param {Object} data - Das zu validierende Objekt
 * @returns {Object} - Validierungsergebnis mit isValid und errors
 */

export function validate(data) {
  const errors = [];

  // Basis-Validierung
  if (!data || typeof data !== 'object') {
    return { isValid: false, errors: ['Eingabeobjekt ist erforderlich'] };
  }

  // Version validieren
  if (!data.version || typeof data.version !== 'number') {
    errors.push('Version ist erforderlich und muss eine Zahl sein');
  }

  // Status validieren
  const validStatuses = ['PREPARED_FOR_SIGNING'];
  if (!data.status || !validStatuses.includes(data.status)) {
    errors.push(`Status ist erforderlich und muss einer der folgenden sein: ${validStatuses.join(', ')}`);
  }

  // Configuration validieren
  const configErrors = validateConfiguration(data.configuration);
  errors.push(...configErrors);

  // Applicant validieren
  const applicantErrors = validateApplicant(data.applicant);
  errors.push(...applicantErrors);

  // ChoosenProductsAndOptions validieren
  const productErrors = validateChosenProductsAndOptions(data.chosenProductsAndOptions);
  errors.push(...productErrors);

  // Modalities validieren
  const modalityErrors = validateModalities(data.modalities);
  errors.push(...modalityErrors);

  // AcceptedTermsAndConditions validieren
  if (typeof data.acceptedTermsAndConditions !== 'boolean') {
    errors.push('acceptedTermsAndConditions ist erforderlich und muss ein boolean sein');
  }

  // Payment validieren (spezifisch für QualifiedApplicationRequest)
  const paymentErrors = validatePayment(data.payment);
  errors.push(...paymentErrors);

  return {
    isValid: errors.length === 0,
    errors: errors,
  };
}

function validateConfiguration(config) {
  const errors = [];

  if (!config) {
    errors.push('Configuration ist erforderlich');
    return errors;
  }

  // Language Code validieren (ISO-639-1)
  if (!config.languageCode || typeof config.languageCode !== 'string') {
    errors.push('Configuration.languageCode ist erforderlich (ISO-639-1)');
  }

  // Currency Code validieren (ISO-4217)
  if (!config.currencyCode || typeof config.currencyCode !== 'string') {
    errors.push('Configuration.currencyCode ist erforderlich (ISO-4217)');
  }

  return errors;
}

function validateApplicant(applicant) {
  const errors = [];

  if (!applicant) {
    errors.push('Applicant ist erforderlich');
    return errors;
  }

  // Title validieren
  if (!applicant.sex || typeof applicant.sex !== 'string') {
    errors.push('Applicant.gender ist erforderlich');
  }

  // Firstname validieren
  if (!applicant.firstname || typeof applicant.firstname !== 'string') {
    errors.push('Applicant.firstname ist erforderlich');
  }

  // Surname validieren
  if (!applicant.surname || typeof applicant.surname !== 'string') {
    errors.push('Applicant.surname ist erforderlich');
  }

  // Birthdate validieren (ISO-8601)
  if (!applicant.birthdate || !isValidISO8601Date(applicant.birthdate)) {
    errors.push('Applicant.birthdate ist erforderlich (ISO-8601 Format)');
  }

  // NationalityCode validieren (ISO-3166-1-alpha2)
  if (!applicant.nationalityCode || typeof applicant.nationalityCode !== 'string') {
    errors.push('Applicant.nationalityCode ist erforderlich (ISO-3166-1-alpha2)');
  }

  // Address validieren
  const addressErrors = validateAddress(applicant.address);
  errors.push(...addressErrors.map(err => `Applicant.${err}`));

  // Contact validieren
  const contactErrors = validateContact(applicant.contact);
  errors.push(...contactErrors.map(err => `Applicant.${err}`));

  return errors;
}

function validateAddress(address) {
  const errors = [];

  if (!address) {
    errors.push('address ist erforderlich');
    return errors;
  }

  if (!address.street || typeof address.street !== 'string') {
    errors.push('address.street ist erforderlich');
  }

  if (!address.streetNo || typeof address.streetNo !== 'string') {
    errors.push('address.streetNo ist erforderlich');
  }

  if (!address.zip || typeof address.zip !== 'string') {
    errors.push('address.zip ist erforderlich');
  }

  if (!address.city || typeof address.city !== 'string') {
    errors.push('address.city ist erforderlich');
  }

  if (!address.countryCode || typeof address.countryCode !== 'string') {
    errors.push('address.countryCode ist erforderlich (ISO-3166-1-alpha2)');
  }

  return errors;
}

function validateContact(contact) {
  const errors = [];

  if (!Array.isArray(contact)) {
    errors.push('contact muss ein Array sein');
    return errors;
  }

  if (contact.length === 0) {
    errors.push('contact darf nicht leer sein');
    return errors;
  }

  contact.forEach((item, index) => {
    if (!item.type || typeof item.type !== 'string') {
      errors.push(`contact[${index}].type ist erforderlich`);
    }
    if (item.type === 'email') {
      if (item.data && !validateEmail(item.data)) {
        errors.push('Emailadresse ist ungültig');
      }
    }

    if (!item.data || typeof item.data !== 'string') {
      errors.push(`contact[${index}].data ist erforderlich`);
    }
  });

  return errors;
}

function validateChosenProductsAndOptions(products) {
  const errors = [];

  if (!Array.isArray(products)) {
    errors.push('chosenProductsAndOptions muss ein Array sein');
    return errors;
  }

  if (products.length === 0) {
    errors.push('chosenProductsAndOptions darf nicht leer sein');
    return errors;
  }

  products.forEach((product, index) => {
    if (!product.code || typeof product.code !== 'string') {
      errors.push(`chosenProductsAndOptions[${index}].code ist erforderlich`);
    }

    if (product.price !== undefined && (typeof product.premium !== 'object' || product.premium.gross < 0)) {
      errors.push(`chosenProductsAndOptions[${index}].premium.gross muss eine positive Zahl sein`);
    }
  });

  return errors;
}

function validateModalities(modalities) {
  const errors = [];

  if (!modalities) {
    errors.push('Modalities ist erforderlich');
    return errors;
  }

  // Period validieren
  if (!modalities.period) {
    errors.push('modalities.period ist erforderlich');
  } else {
    const validPeriods = [
      'ONE_TIME',
      'MONTHLY_CONTINUOUS',
      'QUARTERLY_CONTINUOUS',
      'SIX_MONTH_CONTINUOUS',
      'ANNUALLY_CONTINUOUS',
      'TWO_YEARS_ONE_TIME',
      'THREE_YEAR_ONE_TIME',
      'FIVE_YEARS_ONE_TIME',
      'TEN_RATES_MONTHLY',
      'FIRST_BANKTRANSFER_THEN_ANNUALLY_CONTINUOUS',
    ];

    if (!modalities.period.code || !validPeriods.includes(modalities.period.code)) {
      errors.push(`modalities.period.code muss einer der folgenden sein: ${validPeriods.join(', ')}`);
    }
  }

  // BeginDate validieren
  if (!modalities.beginDate || !isValidISO8601Date(modalities.beginDate)) {
    errors.push('modalities.beginDate ist erforderlich (ISO-8601 Format)');
  }

  return errors;
}

function validatePayment(payment) {
  const errors = [];

  if (!payment) {
    errors.push('Payment ist erforderlich');
    return errors;
  }

  // Payment Type validieren
  if (!payment.type || typeof payment.type !== 'string') {
    errors.push('payment.type ist erforderlich');
  }

  // Payment Data validieren
  if (!payment.data) {
    errors.push('payment.data ist erforderlich');
  } else {
    if (payment.type === 'debit') {
      if (!payment.data.iban || typeof payment.data.iban !== 'string') {
        errors.push('payment.data.iban ist erforderlich für Lastschrift');
      } else if (!validateIBAN(payment.data.iban)) {
        errors.push('IBAN ist nicht gültig');
      }

      if (!payment.data.accountOwner || typeof payment.data.accountOwner !== 'string') {
        errors.push('Kontoinhaber ist erforderlich für Lastschrift');
      }
    }
  }

  return errors;
}

function isValidISO8601Date(dateString) {
  if (typeof dateString !== 'string') return false;

  const iso8601Regex = /^\d{4}-\d{2}-\d{2}$/;
  if (!iso8601Regex.test(dateString)) return false;

  const date = new Date(dateString);
  return date instanceof Date && !isNaN(date.getTime());
}

// Hilfsfunktion für spezifische Validierungen
export function validateIBAN(iban) {
  if (!iban || typeof iban !== 'string') return false;

  const cleanIban = iban.replace(/\s/g, '').toUpperCase();
  const ibanRegex = /^[A-Z]{2}[0-9]{2}[A-Z0-9]+$/;

  return ibanRegex.test(cleanIban) && cleanIban.length >= 15 && cleanIban.length <= 34;
}

export function validateEmail(email) {
  if (!email || typeof email !== 'string') return false;

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
}
